// src/components/ServiceRecovery/FeedbackAnalytics.js
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import serviceRecoveryApi from './serviceRecoveryApi';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const FeedbackAnalytics = () => {
  // Log screen view
  useScreenLogging('FeedbackAnalytics', {
    component: 'FeedbackAnalytics',
  });
  const [analytics, setAnalytics] = useState(null);
  const [recoveryAnalytics, setRecoveryAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const fetchIdRef = useRef(0);

  const fetchAnalytics = useCallback(async () => {
    const currentFetchId = ++fetchIdRef.current;
    try {
      setIsLoading(true);
      const [feedbackData, recoveryData] = await Promise.all([
        serviceRecoveryApi.getFeedbackAnalytics(dateRange).catch(err => {
          console.error('Error fetching feedback analytics:', err);
          return null;
        }),
        serviceRecoveryApi.getRecoveryActionsAnalytics(dateRange).catch(err => {
          console.error('Error fetching recovery analytics:', err);
          return null;
        }),
      ]);

      // Ignore response if a newer fetch was started (avoid stale date range overwriting)
      if (currentFetchId !== fetchIdRef.current) return;

      console.log('📊 FeedbackAnalytics: Raw feedbackData:', feedbackData);
      console.log('📊 FeedbackAnalytics: Raw recoveryData:', recoveryData);

      // Transform API response to match component expectations
      let transformedFeedbackData = transformFeedbackData(feedbackData);
      const transformedRecoveryData = transformRecoveryData(recoveryData);

      // If feedback analytics has no trend data but recovery has analytics.breakdown, use recovery for trend so chart shows
      if (!transformedFeedbackData && recoveryData?.analytics?.breakdown) {
        transformedFeedbackData = transformFeedbackData(recoveryData);
      }

      console.log('📊 FeedbackAnalytics: Transformed feedbackData:', transformedFeedbackData);
      console.log('📊 FeedbackAnalytics: Transformed recoveryData:', transformedRecoveryData);

      setAnalytics(transformedFeedbackData);
      setRecoveryAnalytics(transformedRecoveryData);
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error('Error fetching analytics:', err);
      setAnalytics(null);
      setRecoveryAnalytics(null);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Transform API response to expected format
  // Handles both: 1) { analytics: { total_feedback, average_sentiment, breakdown } } and 2) array / data.feedback
  const transformFeedbackData = (data) => {
    if (!data) return null;

    const analytics = data.analytics || data.data?.analytics || data;
    const feedbackArray = Array.isArray(data) ? data : (data.feedback || data.feedbacks || []);

    // Backend format: { analytics: { total_feedback, average_sentiment, breakdown: { food, service, ambiance, other } } }
    if (analytics && typeof analytics === 'object' && !Array.isArray(analytics)) {
      const total = Number(analytics.total_feedback) || 0;
      const avgSentiment = Number(analytics.average_sentiment) ?? 0;
      const b = analytics.breakdown || {};
      // Support both lowercase and uppercase keys from API
      const Food = Number(b.food ?? b.Food) || 0;
      const Service = Number(b.service ?? b.Service) || 0;
      const Cleanliness = Number(b.ambiance ?? b.Ambiance ?? b.cleanliness ?? b.Cleanliness) || 0;
      const other = Number(b.other ?? b.Other) || 0;
      // Approximate sentiment distribution from average (-1 to 1) and total
      const positive = Math.round(total * (avgSentiment > 0 ? Math.min(1, avgSentiment + 0.3) : 0.2));
      const negative = Math.round(total * (avgSentiment < 0 ? Math.min(1, -avgSentiment + 0.3) : 0.2));
      const neutral = Math.max(0, total - positive - negative);
      // Use a valid date so the trend chart x-axis doesn't show "Invalid Date"
      const dateStr = new Date().toISOString().split('T')[0];
      return {
        trend_by_category: [{ date: dateStr, Food, Service, Cleanliness, other }],
        sentiment_distribution: { positive, neutral, negative },
      };
    }

    if (feedbackArray.length === 0) return null;

    const dateCategoryMap = {};
    const sentimentCounts = { positive: 0, neutral: 0, negative: 0 };

    feedbackArray.forEach((feedback) => {
      const date = feedback.created_at ? new Date(feedback.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const category = feedback.feedback_category || 'other';
      const sentiment = feedback.sentiment_score !== undefined ? feedback.sentiment_score : 0;

      if (!dateCategoryMap[date]) {
        dateCategoryMap[date] = { date, Food: 0, Service: 0, Cleanliness: 0, other: 0 };
      }
      if (category.toLowerCase() === 'food') dateCategoryMap[date].Food++;
      else if (category.toLowerCase() === 'service') dateCategoryMap[date].Service++;
      else if (category.toLowerCase() === 'cleanliness' || category.toLowerCase() === 'ambiance') dateCategoryMap[date].Cleanliness++;
      else dateCategoryMap[date].other++;

      if (sentiment > 0.1) sentimentCounts.positive++;
      else if (sentiment < -0.1) sentimentCounts.negative++;
      else sentimentCounts.neutral++;
    });

    const trendByCategory = Object.values(dateCategoryMap).sort((a, b) => new Date(a.date) - new Date(b.date));
    return {
      trend_by_category: trendByCategory,
      sentiment_distribution: sentimentCounts,
    };
  };

  // Transform recovery analytics data
  // Handles both: 1) { analytics: { total_feedback, recovery_rate, ... } } and 2) array / data.feedback
  const transformRecoveryData = (data) => {
    if (!data) return null;

    const analytics = data.analytics || data;
    const feedbackArray = Array.isArray(data) ? data : (data.feedback || data.feedbacks || []);

    // Backend format: { analytics: { total_feedback, recovery_rate, average_sentiment, breakdown } }
    if (analytics && typeof analytics === 'object' && !Array.isArray(analytics)) {
      const total = Number(analytics.total_feedback) || 0;
      const recoveryRate = Number(analytics.recovery_rate) ?? 0;
      return {
        recovery_actions: [
          {
            action_type: 'Overall',
            count: total,
            avg_sentiment_change: recoveryRate,
            success_rate: recoveryRate,
          },
        ],
        recovery_rate: recoveryRate,
        total_feedback: total,
      };
    }

    if (feedbackArray.length === 0) return null;

    const actionMap = {};
    feedbackArray.forEach((feedback) => {
      const actionType = feedback.recovery_action_taken || 'none';
      const sentimentChange = feedback.guest_sentiment_change_index || 0;
      if (!actionMap[actionType]) {
        actionMap[actionType] = { action_type: actionType, count: 0, total_sentiment_change: 0, successful_count: 0 };
      }
      actionMap[actionType].count++;
      actionMap[actionType].total_sentiment_change += sentimentChange;
      if (sentimentChange > 0.2) actionMap[actionType].successful_count++;
    });

    const recoveryActions = Object.values(actionMap).map((action) => ({
      action_type: action.action_type === 'none' ? 'No Action' : action.action_type,
      count: action.count,
      avg_sentiment_change: action.total_sentiment_change / action.count,
      success_rate: action.successful_count / action.count,
    }));
    return { recovery_actions: recoveryActions };
  };

  // Mock data for development - COMMENTED OUT
  // const getMockFeedbackAnalytics = () => ({
  //   trend_by_category: [
  //     { date: '2024-01-01', Food: 45, Service: 32, Cleanliness: 28 },
  //     { date: '2024-01-02', Food: 52, Service: 38, Cleanliness: 31 },
  //     { date: '2024-01-03', Food: 48, Service: 35, Cleanliness: 29 },
  //     { date: '2024-01-04', Food: 55, Service: 42, Cleanliness: 33 },
  //     { date: '2024-01-05', Food: 50, Service: 40, Cleanliness: 30 },
  //   ],
  //   sentiment_distribution: {
  //     positive: 320,
  //     neutral: 180,
  //     negative: 100,
  //   },
  // });

  // const getMockRecoveryAnalytics = () => ({
  //   recovery_actions: [
  //     {
  //       action_type: 'compensation',
  //       count: 25,
  //       avg_sentiment_change: 0.45,
  //       success_rate: 0.85,
  //     },
  //     {
  //       action_type: 'apology',
  //       count: 18,
  //       avg_sentiment_change: 0.32,
  //       success_rate: 0.72,
  //     },
  //     {
  //       action_type: 'refund',
  //       count: 12,
  //       avg_sentiment_change: 0.58,
  //       success_rate: 0.92,
  //     },
  //     {
  //       action_type: 'follow_up',
  //       count: 30,
  //       avg_sentiment_change: 0.28,
  //       success_rate: 0.65,
  //     },
  //   ],
  // });

  // Trend chart by category
  const trendChartData = useMemo(() => {
    if (!analytics?.trend_by_category?.length) return null;

    const rows = analytics.trend_by_category;
    // With a single point, duplicate it so the line draws (Chart.js needs 2+ points for a visible line)
    const useRows = rows.length === 1 ? [rows[0], rows[0]] : rows;
    const labels = useRows.map((item) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return item.date || 'Summary';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Food',
          data: useRows.map((item) => Number(item.Food) ?? 0),
          borderColor: '#22C55E',
          backgroundColor: '#22C55E20',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Service',
          data: useRows.map((item) => Number(item.Service) ?? 0),
          borderColor: '#3B82F6',
          backgroundColor: '#3B82F620',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Cleanliness',
          data: useRows.map((item) => Number(item.Cleanliness) ?? 0),
          borderColor: '#8B5CF6',
          backgroundColor: '#8B5CF620',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  }, [analytics]);

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148, 163, 184, 0.2)' },
        ticks: { stepSize: 1 },
      },
      x: {
        grid: { display: false },
      },
    },
  };

  // Sentiment distribution chart
  const sentimentChartData = useMemo(() => {
    if (!analytics?.sentiment_distribution) return null;

    return {
      labels: ['Positive', 'Neutral', 'Negative'],
      datasets: [
        {
          data: [
            analytics.sentiment_distribution.positive,
            analytics.sentiment_distribution.neutral,
            analytics.sentiment_distribution.negative,
          ],
          backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
          borderWidth: 0,
        },
      ],
    };
  }, [analytics]);

  const sentimentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className={typography.p} style={{ color: '#000000' }}>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className={typography.h2} style={{ color: '#000000' }}>
          Feedback Analytics
        </h2>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <input
            type="date"
            value={dateRange.startDate || ''}
            onChange={(e) =>
              setDateRange({ ...dateRange, startDate: e.target.value })
            }
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <input
            type="date"
            value={dateRange.endDate || ''}
            onChange={(e) =>
              setDateRange({ ...dateRange, endDate: e.target.value })
            }
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Trend Chart by Category */}
      <Card.Gradient className="p-6">
        <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1.5rem' }}>
          Feedback Trend by Category
        </h3>
        {trendChartData ? (
          <div style={{ height: '300px' }}>
            <Line
              key={`trend-${dateRange.startDate || ''}-${dateRange.endDate || ''}`}
              data={trendChartData}
              options={trendChartOptions}
            />
          </div>
        ) : (
          <p className="text-gray-500">No trend data available</p>
        )}
      </Card.Gradient>

      {/* Sentiment Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card.Gradient className="p-6">
          <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1.5rem' }}>
            Sentiment Distribution
          </h3>
          {sentimentChartData ? (
            <div style={{ height: '300px' }}>
              <Doughnut
                key={`sentiment-${dateRange.startDate || ''}-${dateRange.endDate || ''}`}
                data={sentimentChartData}
                options={sentimentChartOptions}
              />
            </div>
          ) : (
            <p className="text-gray-500">No sentiment data available</p>
          )}
        </Card.Gradient>

        {/* Sentiment Summary */}
        <Card.Gradient className="p-6">
          <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1.5rem' }}>
            Summary
          </h3>
          {analytics?.sentiment_distribution && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="font-semibold text-green-800">Positive</span>
                <span className="text-2xl font-bold text-green-600">
                  {analytics.sentiment_distribution.positive}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <span className="font-semibold text-yellow-800">Neutral</span>
                <span className="text-2xl font-bold text-yellow-600">
                  {analytics.sentiment_distribution.neutral}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <span className="font-semibold text-red-800">Negative</span>
                <span className="text-2xl font-bold text-red-600">
                  {analytics.sentiment_distribution.negative}
                </span>
              </div>
            </div>
          )}
        </Card.Gradient>
      </div>

      {/* Recovery Actions Table */}
      <Card.Gradient className="p-6">
        <h3 className={typography.h3} style={{ color: '#000000', marginBottom: '1.5rem' }}>
          Recovery Actions vs Outcome
        </h3>
        {recoveryAnalytics?.recovery_actions ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left p-3 font-semibold" style={{ color: '#000000' }}>
                    Action Type
                  </th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#000000' }}>
                    Count
                  </th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#000000' }}>
                    Avg Sentiment Change
                  </th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#000000' }}>
                    Success Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {recoveryAnalytics.recovery_actions.map((action, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 capitalize">{action.action_type}</td>
                    <td className="p-3">{action.count}</td>
                    <td className="p-3">
                      <span
                        className={`font-semibold ${
                          action.avg_sentiment_change > 0.4
                            ? 'text-green-600'
                            : action.avg_sentiment_change > 0.2
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {action.avg_sentiment_change.toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`font-semibold ${
                          action.success_rate > 0.8
                            ? 'text-green-600'
                            : action.success_rate > 0.6
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {(action.success_rate * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No recovery action data available</p>
        )}
      </Card.Gradient>
    </div>
  );
};

export default FeedbackAnalytics;
